import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, RecurlyProvider } from '@recurly/react-recurly';
import { useHistory, useParams, Route, Switch } from 'react-router-dom';
import { HeaderBar } from 'components/HeaderBar';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import PageLayout from 'components/PageLayout';
import { ENV } from 'shared/ENV';
import { useAuthState } from 'state/useAuthState';
import { useOrganizationState } from 'features/organizations/state/useOrganizationState';
import { OrganizationUpgradeSeatsForm } from 'features/organizations/components/OrganizationUpgradeSeatsForm';
import { OrganizationUpgradeBillingInfoForm } from 'features/organizations/components/OrganizationUpgradeBillingInfoForm';
import { OrganizationSuccessMessage } from 'features/organizations/components/OrganizationSuccessMessage';

type UpgradeFlowStep = 'seats' | 'billing' | 'success';

export const OrganizationUpgradeFlow = (): JSX.Element => {
  const { t } = useTranslation();
  const { step = 'seats' } = useParams<{ step?: UpgradeFlowStep }>();
  const history = useHistory();
  const { user } = useAuthState();

  const {
    updateOrganizationState,
    resetOrganizationState,
    ...organizationState
  } = useOrganizationState();

  const totalSeats =
    (organizationState.proSeats || 0) + (organizationState.regularSeats || 0);

  // In case the user visits this page directly to jump ahead, redirect them
  useEffect(() => {
    if (step === 'billing' && totalSeats === 0) {
      history.push('/organization/upgrade/');
    }
  }, [history, step, totalSeats]);

  const firstName = organizationState.firstName || user?.firstName;
  const lastName = organizationState.lastName || user?.lastName;
  const email = organizationState.email || user?.email;
  useEffect(() => {
    if (step === 'seats' && totalSeats === 0) {
      updateOrganizationState({
        firstName,
        lastName,
        email,
        proSeats: 0,
        regularSeats: 0,
      });
    }
  }, [updateOrganizationState, step, totalSeats, firstName, lastName, email]);

  const navigateToStep = (newStep: UpgradeFlowStep): void => {
    history.push(`/organization/upgrade/${newStep === 'seats' ? '' : newStep}`);
  };

  const getHeaderAction = (): string => {
    if (step === 'seats') {
      return '/organization';
    }
    if (step === 'success') {
      return 'noControls';
    }
    return 'back';
  };

  return (
    <PageLayout data-cname="OrganizationUpgradeFlow" showBanner={false}>
      {{
        headerBar: (
          <HeaderBar
            title={t('organization.manageMemberships')}
            action={getHeaderAction()}
          />
        ),
        content: (
          <SimplePageLayout>
            <RecurlyProvider publicKey={ENV.VITE_RECURLY_PUBLIC_TOKEN}>
              <Elements>
                <Switch>
                  <Route exact path="/organization/upgrade/">
                    <OrganizationUpgradeSeatsForm
                      onSubmit={(data) => {
                        updateOrganizationState({
                          ...data,
                        });
                        navigateToStep('billing');
                      }}
                    />
                  </Route>
                  <Route path="/organization/upgrade/billing/:new?">
                    <OrganizationUpgradeBillingInfoForm
                      onSubmit={(data) => {
                        updateOrganizationState({
                          ...data,
                        });
                        navigateToStep('success');
                      }}
                    />
                  </Route>
                  <Route path="/organization/upgrade/success">
                    <OrganizationSuccessMessage
                      headingText={t('organizationPurchase.success.title')}
                      subheadingText={t('organizationPurchase.success.thanks')}
                      buttonText={t('organizationPurchase.success.button')}
                      to="/organization"
                    />
                  </Route>
                </Switch>
              </Elements>
            </RecurlyProvider>
          </SimplePageLayout>
        ),
      }}
    </PageLayout>
  );
};
